import { useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Minus, Maximize2, Minimize2 } from 'lucide-react';
import {
  useFloatingPanelBounds,
  type PanelBounds,
  type PanelMinSize,
} from './useFloatingPanelBounds';
import {
  closeTopmostFloatingPanel,
  focusFloatingPanel,
  registerFloatingPanel,
} from './floatingPanelStack';

export type FloatingWorkspaceBackdrop = 'full' | 'none';

export interface FloatingWorkspacePanelProps {
  panelId?: string;
  title: string;
  subtitle?: ReactNode;
  icon?: ReactNode;
  defaultBounds: PanelBounds;
  minSize?: PanelMinSize;
  backdrop?: FloatingWorkspaceBackdrop;
  /** When true, clicking the dimmed backdrop calls onClose */
  backdropDismiss?: boolean;
  baseZIndex?: number;
  onClose: () => void;
  headerActions?: ReactNode;
  headerLeading?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}

const DEFAULT_MIN: PanelMinSize = { width: 480, height: 320 };

export function FloatingWorkspacePanel({
  panelId,
  title,
  subtitle,
  icon,
  defaultBounds,
  minSize = DEFAULT_MIN,
  backdrop = 'full',
  backdropDismiss = false,
  baseZIndex = 9999,
  onClose,
  headerActions,
  headerLeading,
  children,
  footer,
}: FloatingWorkspacePanelProps) {
  const autoId = useId();
  const id = panelId || autoId.replace(/:/g, '');
  const [zIndex, setZIndex] = useState(baseZIndex);

  const {
    bounds,
    isMaximized,
    isMinimized,
    startDrag,
    startResize,
    toggleMaximize,
    minimize,
    restore,
  } = useFloatingPanelBounds(panelId || id, defaultBounds, minSize);

  useEffect(() => {
    return registerFloatingPanel(id, baseZIndex, onClose);
  }, [id, baseZIndex, onClose]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        closeTopmostFloatingPanel();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const bringToFront = () => {
    setZIndex(focusFloatingPanel(id));
  };

  const chromeButtons = (
    <div className="flex items-center gap-0.5">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          minimize();
        }}
        className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
        title="Minimize"
      >
        <Minus className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleMaximize();
        }}
        className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
        title={isMaximized ? 'Restore' : 'Maximize'}
      >
        {isMaximized ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="rounded-lg p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"
        title="Close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  if (isMinimized) {
    return createPortal(
      <>
        <div
          className="fixed bottom-4 right-4 flex max-w-[min(420px,90vw)] items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-lg"
          style={{ zIndex }}
        >
          <span className="truncate text-sm font-medium text-slate-800">{title}</span>
          <button
            type="button"
            onClick={restore}
            className="shrink-0 rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200"
          >
            Restore
          </button>
          <button type="button" onClick={onClose} className="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="hidden" aria-hidden="true">
          {children}
          {footer}
        </div>
      </>,
      document.body,
    );
  }

  const panel = (
    <>
      {backdrop === 'full' && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-[2px]"
          style={{ zIndex: zIndex - 1, pointerEvents: backdropDismiss ? 'auto' : 'none' }}
          onMouseDown={backdropDismiss ? onClose : undefined}
          aria-hidden
        />
      )}
      <div
        className="fixed flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-2xl"
        style={{
          left: bounds.x,
          top: bounds.y,
          width: bounds.width,
          height: bounds.height,
          zIndex,
        }}
        onMouseDown={bringToFront}
        role="dialog"
        aria-label={title}
      >
        <div
          className="flex shrink-0 cursor-move items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-3 select-none"
          onPointerDown={startDrag}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {headerLeading}
            {icon}
            <div className="min-w-0">
              <h2 className="truncate text-base font-bold text-slate-800">{title}</h2>
              {subtitle && <div className="truncate text-xs text-slate-500">{subtitle}</div>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2" onPointerDown={(e) => e.stopPropagation()}>
            {headerActions}
            {chromeButtons}
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>

        {footer && <div className="shrink-0 border-t border-slate-200 bg-white">{footer}</div>}

        {!isMaximized && (
          <>
            <div
              className="absolute bottom-0 right-0 h-4 w-4 cursor-se-resize"
              onPointerDown={(e) => startResize(e, 'se')}
              aria-hidden
            />
            <div
              className="absolute bottom-0 left-3 right-3 h-1.5 cursor-s-resize"
              onPointerDown={(e) => startResize(e, 's')}
              aria-hidden
            />
            <div
              className="absolute right-0 top-12 bottom-3 w-1.5 cursor-e-resize"
              onPointerDown={(e) => startResize(e, 'e')}
              aria-hidden
            />
          </>
        )}
      </div>
    </>
  );

  return createPortal(panel, document.body);
}
